import React, { useMemo } from 'react';
import {
  find,
  isObject,
  keyBy,
  max,
  mergeWith,
  toArray,
  trimEnd,
} from 'lodash';
import moment from 'moment';
import copy from 'copy-to-clipboard';
import { useNotify } from 'js/hooks/useNotify';
import { useChartSearchParam } from 'js/hooks/useChartSearchParam';
import { useAssessmentBreakoutSelection } from 'js/hooks/useAssessmentBreakoutSelection';
import {
  AssessmentReviewBreakoutRoutes,
  BreakoutSelect,
} from 'js/components/Breakouts';
import { ChartSelector, PanelHeader, Icon, IconStack } from 'js/components';
import { titleCase } from 'js/utils/string';
import { getSummaryMetrics } from 'js/utils/question-set';

import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  UncontrolledDropdown,
  DropdownMenu,
  DropdownToggle,
  DropdownItem,
  Nav,
  NavItem,
  NavLink,
} from 'reactstrap';
import { Link, Route, Switch, useLocation } from 'react-router-dom';

import QuestionOverview, { TILE_MODE } from '../QuestionOverview';
import AssessmentDetailChart, {
  Glyph as LollipopGlyph,
} from './AssessmentDetailLineChart';
import AssessmentReviewDetailResults from './AssessmentReviewDetailResults';
import ParticipationStats from 'js/engines/SurveyAssessment/components/Review/ParticipationStats';
import AssessmentHistoryRadialPieChart, {
  Glyph as RadialPieGlyph,
} from 'js/engines/SurveyAssessment/components/Review/AssessmentHistoryRadialPieChart';
import { Glyph as RadarGlyph } from 'js/components/Charts/SeriesRadarChart';
import CohortSeriesRadarChart from 'js/engines/SurveyAssessment/components/Review/CohortSeriesRadarChart';
import { isArchived } from 'js/utils/team-utils';

import {
  faCopy,
  faClipboardListCheck,
  faPoll,
} from '@fortawesome/pro-regular-svg-icons';
import { faCircle, faRedoAlt } from '@fortawesome/pro-solid-svg-icons';

function buildCategoryLookups(assessments, supplementalCategories) {
  const configCategories = (assessments ?? [])
    .map((a) => a?.config?.categories)
    .filter((x) => isObject(x))
    .reduce(
      (acc, categories) =>
        mergeWith(acc, categories, (obj, src) =>
          obj !== undefined ? obj : src
        ),
      {}
    );

  const allCategories = mergeWith(
    configCategories,
    supplementalCategories,
    (obj, src, key) =>
      obj !== undefined ? obj : { display_name: titleCase(key) }
  );

  const categoryMap = new Map(
    Object.entries(allCategories).sort(
      ([, a], [, b]) => (a?.ordinal ?? 0) - (b?.ordinal ?? 0)
    )
  );

  let ci = 0;
  categoryMap.forEach((value) => {
    value.palette = `palette-cat-${++ci}`;
  });

  return categoryMap;
}

const AssessmentReviewDetail = ({
  assessments,
  team,
  match,
  assessmentInvitesQueryResult,
  onUpdateBreakouts,
  savingBreakouts,
}) => {
  const location = useLocation();
  const assessmentId = match.params.assessmentId;
  const assessment = find(assessments, (a) => a.id === assessmentId);
  const detailBase = trimEnd(match.url, '/').replace(
    /\/(overview|results|breakouts.*)$/,
    ''
  );
  const currentView =
    location.pathname.slice(match.url.length)?.replaceAll('/', '') ||
    'overview';
  const onBreakoutsTab =
    currentView === 'breakouts' || currentView.startsWith('breakouts');
  const breakoutsBase = `${detailBase}/breakouts`;
  const showCharts = currentView === 'overview' || currentView === 'results';
  const showSidebar = currentView !== 'results' && !onBreakoutsTab;
  const [chartKey, setChartKey] = useChartSearchParam();

  const {
    selectedBreakoutId,
    handleBreakoutChange,
    filter,
    availableBreakouts,
    participantCohortMap,
    legendFields,
    activeBreakoutName,
    activeCohort,
    pinnedCohort,
    cohortProps,
    handlePinCohort,
    handleClearPinnedCohort,
    handlePeekCohort,
    handleClearPeekedCohort,
  } = useAssessmentBreakoutSelection(assessment);

  const dataMap = useMemo(
    () =>
      !assessment
        ? null
        : getSummaryMetrics([assessment], {
            filter,
            participantCohortsMap: participantCohortMap,
          }),
    [assessment, filter, participantCohortMap]
  );

  const categoryLookups = useMemo(
    () => buildCategoryLookups(assessments, dataMap?.categories),
    [assessments, dataMap]
  );

  const categoryColorMap = useMemo(() => {
    const map = {};
    categoryLookups.forEach((value, key) => {
      map[key] = value.palette;
    });
    return map;
  }, [categoryLookups]);

  const notify = useNotify();
  const handleCopyLink = () => {
    const url = `${window.location.origin}/assessments/${assessment.id}/run`;

    copy(url);

    notify({ type: 'success', message: 'Link copied to clipboard' });
  };

  const participants = toArray(keyBy(assessment?.participants, 'id')).sort(
    (a, b) =>
      a.name?.localeCompare(b.name, 'en', {
        sensitivity: 'base',
      }) ?? -1
  );

  // TODO: refactor this into a utility method
  // NOTE: this calculation is also used by Host/Overview
  const counts = {};
  if (!!assessment) {
    assessment.question_sets.forEach((qs) => {
      qs.questions.forEach((q) => {
        q.responses.forEach((r) => {
          const key = `${q.id}-${r.value}`;
          counts[key] = counts.hasOwnProperty(key) ? counts[key] + 1 : 1;
        });
      });
    });
  }

  const yLimit = max(toArray(counts)) || 0;
  const timestamp = assessment
    ? moment(assessment.inserted_at).format('MMM D, YYYY h:mm a')
    : null;

  const pendingInvites = assessmentInvitesQueryResult.data?.filter(
    (i) => i.email && i.participant_id === null
  );

  const teamIsArchived = isArchived(team);
  const dropdownProps = teamIsArchived ? { disabled: true, isOpen: false } : {};

  const renderOverviewTiles = () =>
    !assessment
      ? null
      : assessment.question_sets.map((questionSet) => (
          <div key={questionSet.id} className="question-results-summary">
            {questionSet.questions.map((question) => (
              <Link
                key={question.id}
                className="question-result"
                to={`${detailBase}/results${location.search}#${questionSet.key}_${question.key}`}
              >
                <QuestionOverview
                  mode={TILE_MODE.REVIEW}
                  question={question}
                  responses={question.responses}
                  yLimit={yLimit}
                />
              </Link>
            ))}
          </div>
        ));

  return (
    <div className="question-set-history live-assessment-review-detail">
      <PanelHeader size="flex">
        <div className="assessment-review-header">
          <section className="details">
            <div className="container-fluid">
              <div className="row">
                <div className="col-12 col-md-9">
                  <label>Assessment</label>
                  <h1>{(assessment || {}).name}</h1>
                  <div className="timestamp">{timestamp}</div>
                </div>
                <div className="col-12 col-md-3">
                  <div className="actions">
                    <UncontrolledDropdown
                      {...dropdownProps}
                      disabled={teamIsArchived}
                    >
                      <DropdownToggle
                        color="primary"
                        caret
                        disabled={teamIsArchived}
                      >
                        Actions
                      </DropdownToggle>
                      <DropdownMenu right>
                        <DropdownItem
                          tag={Link}
                          to={`/assessments/${assessment.id}/host`}
                          disabled={teamIsArchived}
                        >
                          <Icon
                            icon={faClipboardListCheck}
                            size="lg"
                            className="btn-icon-left"
                            fixedWidth
                          />
                          Facilitate this assessment
                        </DropdownItem>
                        <DropdownItem
                          onClick={handleCopyLink}
                          disabled={teamIsArchived}
                        >
                          <Icon
                            icon={faCopy}
                            size="lg"
                            className="btn-icon-left"
                            fixedWidth
                          />
                          Copy link for participants
                        </DropdownItem>
                        <DropdownItem
                          tag={Link}
                          to={`/w/${team.workspace_id}/teams/${team.id}/assessments/${assessment.key}/new`}
                          disabled={teamIsArchived}
                        >
                          <IconStack
                            size="lg"
                            className="btn-icon-left"
                            fixedWidth
                          >
                            <Icon
                              icon={faCircle}
                              mask={faPoll}
                              transform="shrink-4 right-6 down-6"
                            />
                            <Icon
                              icon={faRedoAlt}
                              transform="shrink-7 right-6 down-6"
                            />
                          </IconStack>
                          Run this assessment again
                        </DropdownItem>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-12 col-md-9">
                  <label>Team</label>
                  <h2>
                    {team.name}
                    &nbsp;
                  </h2>
                  {showCharts ? (
                    <BreakoutSelect
                      availableBreakouts={availableBreakouts}
                      selectedBreakoutId={selectedBreakoutId}
                      onBreakoutChange={handleBreakoutChange}
                    />
                  ) : null}
                </div>
                <div className="col-12 col-md-3">
                  <ParticipationStats
                    assessment={assessment}
                    invites={assessmentInvitesQueryResult}
                  />
                </div>
              </div>
            </div>
          </section>
          {showCharts ? (
            <section className="details-chart aggregate-history container-fluid mt-4">
              <label>Charts</label>
              {assessment === undefined ? null : (
                <ChartSelector
                  activeKey={chartKey}
                  onChange={setChartKey}
                  charts={[
                    {
                      label: LollipopGlyph,
                      component: AssessmentDetailChart,
                      buttonProps: { 'data-chart-type': 'lollipop' },
                      componentProps: { height: 100 },
                    },
                    {
                      label: RadialPieGlyph,
                      component: AssessmentHistoryRadialPieChart,
                      buttonProps: { 'data-chart-type': 'radial-pie' },
                    },
                    {
                      label: RadarGlyph,
                      component: CohortSeriesRadarChart,
                      buttonProps: { 'data-chart-type': 'radar' },
                    },
                  ]}
                  assessmentId={assessmentId}
                  assessment={assessment}
                  dataMap={dataMap}
                  categoryColorMap={categoryColorMap}
                  categoryLookups={categoryLookups}
                  legendFields={legendFields}
                  participantCohortMap={participantCohortMap}
                  activeCohort={activeCohort}
                  pinnedCohort={pinnedCohort}
                  breakoutName={activeBreakoutName}
                  onPinCohort={handlePinCohort}
                  onClearPinnedCohort={handleClearPinnedCohort}
                  onPeekCohort={handlePeekCohort}
                  onClearPeekedCohort={handleClearPeekedCohort}
                  questionSetHref={(questionSetKey) => `#${questionSetKey}`}
                  questionHref={(questionSetKey, questionKey) =>
                    `#${questionSetKey}_${questionKey}`
                  }
                />
              )}
            </section>
          ) : null}
        </div>
        <div className="container-fluid">
          <Nav className="tab-nav nav-underline">
            <NavItem>
              <NavLink
                tag={Link}
                active={currentView === 'overview'}
                data-view="overview"
                to={`${detailBase}${location.search}`}
              >
                Overview
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                tag={Link}
                active={currentView === 'results'}
                data-view="results"
                to={`${detailBase}/results${location.search}`}
              >
                Results
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                tag={Link}
                active={onBreakoutsTab}
                data-view="breakouts"
                to={`${breakoutsBase}${location.search}`}
              >
                Breakouts
              </NavLink>
            </NavItem>
          </Nav>
        </div>
      </PanelHeader>
      <Switch>
        <AssessmentReviewBreakoutRoutes
          path={`${match.path}/breakouts`}
          assessment={assessment}
          onSave={onUpdateBreakouts}
          saving={savingBreakouts}
          categoryColorMap={categoryColorMap}
          listPath={breakoutsBase}
        />
        <Route>
          <div className="content">
            <div className="row">
              {showSidebar && (
                <div className="col col-12 col-md-3 order-md-9">
                  <Card className="participant-card">
                    <CardHeader>
                      <CardTitle>Participants</CardTitle>
                    </CardHeader>
                    <CardBody>
                      <ul className="participant-list">
                        {participants.map((p) => (
                          <li key={p.id} className="participant">
                            <Icon icon="user" />
                            {p.name}
                          </li>
                        ))}
                      </ul>
                    </CardBody>
                    {pendingInvites && pendingInvites.length > 0 && (
                      <>
                        <CardHeader>
                          <CardTitle>Pending invites</CardTitle>
                        </CardHeader>
                        <CardBody>
                          <ul className="participant-list">
                            {pendingInvites?.map((i) => (
                              <li key={i.email} className="participant">
                                <Icon icon="envelope" />
                                {i.email}
                              </li>
                            ))}
                          </ul>
                        </CardBody>
                      </>
                    )}
                  </Card>
                </div>
              )}
              <div
                className={
                  showSidebar ? 'col col-12 col-md-9 order-md-1' : 'col col-12'
                }
              >
                <Switch>
                  <Route
                    path={`${match.path}/overview`}
                    render={() => (
                      <Card>
                        <CardBody className="equal-padding">
                          {renderOverviewTiles()}
                        </CardBody>
                      </Card>
                    )}
                  />
                  <Route
                    path={`${match.path}/results`}
                    render={() => (
                      <AssessmentReviewDetailResults
                        assessment={assessment}
                        detailHrefFor={(question) =>
                          `${detailBase}/questions/${question.id}`
                        }
                        filter={filter}
                        {...cohortProps}
                      />
                    )}
                  />
                  <Route
                    path={match.path}
                    render={() => (
                      <Card>
                        <CardBody className="equal-padding">
                          {renderOverviewTiles()}
                        </CardBody>
                      </Card>
                    )}
                  />
                </Switch>
              </div>
            </div>
          </div>
        </Route>
      </Switch>
    </div>
  );
};

export default AssessmentReviewDetail;
